import { Link } from 'react-router-dom';
import { REVIEW_URL } from './shared';
import { CITIES, cityPath } from '../data/locations';

// Sections shared by /home-owners and the GTA city pages.

const GALLERY = [
  { src: '/photos/before-after-backyard.jpg', w: 1000, h: 844, alt: 'Overgrown backyard before and after mowing', caption: 'Overgrown backyard, cut and trimmed' },
  { src: '/photos/before-after-garden-bed.jpg', w: 1000, h: 844, alt: 'Garden bed before and after weeding', caption: 'Front garden bed, weeded by hand' },
  { src: '/photos/before-after-walkway.jpg', w: 1000, h: 844, alt: 'Snow-filled walkway before and after clearing', caption: 'Walkway cleared after a heavy snowfall' },
  { src: '/photos/before-after-tall-grass.jpg', w: 1000, h: 1000, alt: 'Tall grass before and after a full cut', caption: 'Knee-high grass brought back under control' },
  { src: '/photos/before-after-side-path.jpg', w: 1000, h: 1000, alt: 'Overgrown side path before and after clearing', caption: 'Side path cleared back to the paving' },
  { src: '/photos/before-after-stone-path.jpg', w: 1000, h: 844, alt: 'Backyard with a stone path and cut lawn', caption: 'Back garden mowed around the stonework' },
];

const STEPS = [
  { title: 'Book a quote', text: 'Tell us your address and what needs doing. Photos help but are not required.' },
  { title: 'We price it', text: 'You get a quote for the whole job, not an hourly rate. Approve it and we schedule a student.' },
  { title: 'The work gets done', text: 'Before and after photos come to you. Once you approve, the student gets paid.' },
];

export function TrustBar() {
  return (
    <section className="stat-bar" aria-label="Why Student For Hire">
      <div className="wrap">
        <div className="stat">
          <div className="stat__title">Free quotes</div>
          <div className="stat__text">Book a quote and we price the job for you, no obligation.</div>
        </div>
        <div className="stat">
          <div className="stat__title">ID-checked students</div>
          <div className="stat__text">Photo ID, a selfie and a student card before anyone works.</div>
        </div>
        <div className="stat">
          <div className="stat__title">You approve the work</div>
          <div className="stat__text">Before and after photos on every job, approved by you.</div>
        </div>
      </div>
    </section>
  );
}

// `lead` moves that photo to the front, so city pages open on the relevant job.
export function BeforeAfterGallery({ id, title = 'Real jobs, before and after.', lead }) {
  const photos = lead ? [...GALLERY].sort((a, b) => (b.src === lead) - (a.src === lead)) : GALLERY;
  return (
    <section id={id} className="section section--white">
      <div className="wrap">
        <h2 className="h2">{title}</h2>
        <p className="lede gallery-lede">Every one of these was done by a student on our roster, photographed on the day.</p>
        <div className="gallery">
          {photos.map((g) => (
            <figure key={g.src}>
              <img src={g.src} alt={g.alt} width={g.w} height={g.h} loading="lazy" decoding="async" />
              <figcaption>{g.caption}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks({ id, title = 'Booking takes three steps.', white = false }) {
  return (
    <section id={id} className={`section${white ? ' section--white' : ''}`}>
      <div className="wrap">
        <h2 className="h2 steps-title">{title}</h2>
        <div className="hairline-grid">
          {STEPS.map((s, i) => (
            <div key={s.title} className="hairline-grid__cell">
              <div className="step-num">{String(i + 1).padStart(2, '0')}</div>
              <h3 className="h3">{s.title}</h3>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AreasWeServe({ title = 'Areas we serve across the GTA.', exclude, white = false }) {
  return (
    <section id="areas" className={`section${white ? ' section--white' : ''}`}>
      <div className="wrap">
        <h2 className="h2">{title}</h2>
        <p className="lede">
          Students take on lawn mowing, leaf cleanups, snow removal and more across Toronto and the Greater Toronto Area.
          Pick your city to see what we do there.
        </p>
        <ul className="area-grid">
          {CITIES.filter((c) => c.slug !== exclude).map((c) => (
            <li key={c.slug}>
              <Link to={cityPath(c)} className="area-link">{c.name}</Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// Pair with faqJsonLd(items) in the page's <Seo> so the markup matches.
export function Faq({ id, title, items, white = false }) {
  return (
    <section id={id} className={`section${white ? ' section--white' : ''}`}>
      <div className="wrap">
        <h2 className="h2 steps-title">{title}</h2>
        <div className="faq">
          {items.map((f) => (
            <details key={f.q} className="faq__item">
              <summary className="faq__q">{f.q}</summary>
              <p className="faq__a">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// Secondary to the "Get a free quote" CTA, so it uses the outline button.
export function ReviewPrompt({ white = false }) {
  return (
    <section className={`section section--center review-prompt${white ? ' section--white' : ''}`}>
      <div className="wrap">
        <h2 className="h2">Had a student work on your home?</h2>
        <p>Tell other homeowners how it went. A quick Google review helps more neighbours find a student.</p>
        <a href={REVIEW_URL} target="_blank" rel="noopener noreferrer" className="btn btn--outline-navy">
          Leave a review<span className="visually-hidden"> on Google (opens in a new tab)</span>
        </a>
      </div>
    </section>
  );
}
